/**
 * Enhanced Team Management Component for AgenticRCM
 * Full feature parity with SaaSFoundation Team Management
 * Includes: Member list, invitations, role management, organization settings
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, UserPlus, Mail, Shield, X, CheckCircle, AlertCircle, 
  Building2, Settings, Calendar, Clock, RefreshCw, HelpCircle,
  ChevronRight, Copy
} from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { readApiError } from '@saas-foundation/shared-frontend/utils/upgradeErrors';

// Get API base URL - uses nginx proxy to SaaSFoundation
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const port = window.location.port;
    const hostname = window.location.hostname;
    
    // Always use relative URL for nginx proxy when not on localhost:3000/3001 (development)
    // This ensures Authorization headers are properly forwarded through nginx
    if (hostname === 'localhost' && (port === '3000' || port === '3001')) {
      // Only for direct development (SaaSFoundation app), use explicit port
      return 'http://localhost:8001/api/v1';
    }
    
    // For all other cases (containerized, production, or AgenticRCM on 3160), use nginx proxy
    return '/api/v1'; // nginx proxies /api to SaaSFoundation:8001
  }
  
  // Fallback: use nginx proxy
  return '/api/v1';
};

const TeamManagement = () => {
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Team data state
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState('member');
  
  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member'
  });
  
  // Help modal state
  const [showRoleHelp, setShowRoleHelp] = useState(false);
  
  // Organization form state
  const [orgForm, setOrgForm] = useState({
    name: '',
    description: '',
    organization_type: 'solo'
  });

  // Locations state
  const [locations, setLocations] = useState([]);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [locationForm, setLocationForm] = useState({
    name: '', address: '', address_line2: '', city: '', state: '', postal_code: '', country: 'US',
    phone: '', timezone: 'America/New_York', is_primary: false
  });
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [deleteLocationTarget, setDeleteLocationTarget] = useState(null);
  const [showDeleteLocationModal, setShowDeleteLocationModal] = useState(false);

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchTeamData();
  }, []);

  // Must run before any early return — sync org form when organization loads
  useEffect(() => {
    if (organization && (!orgForm.name || orgForm.name === '')) {
      setOrgForm({
        name: organization.name || '',
        description: organization.description || '',
        organization_type: organization.organization_type || 'solo',
      });
    }
  }, [organization, orgForm.name]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      console.log('[TeamManagement] Fetching team data...', { 
        API_BASE_URL, 
        hasToken: !!token,
        userEmail: user?.email 
      });
      
      // Create headers once to ensure consistency
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[TeamManagement] Token found, length:', token.length, 'First 20 chars:', token.substring(0, 20));
      } else {
        console.warn('[TeamManagement] No token found in localStorage!');
      }
      
      // Get organization_id from localStorage/context so backend can return this org when user has access
      let organizationId = localStorage.getItem('organizationId') || 
                          localStorage.getItem('selectedOrganizationId') ||
                          (user?.organization_id);
      
      console.log('[TeamManagement] Organization ID from localStorage/user:', organizationId);
      
      // Request current org; pass organization_id so backend returns this org when user is a member (Team Management context)
      const currentUrl = `${API_BASE_URL}/organizations/current${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''}`;
      console.log('[TeamManagement] Making request to:', currentUrl);
      console.log('[TeamManagement] Request headers:', headers);
      const orgResponse = await fetch(currentUrl, {
        method: 'GET',
        headers: { ...headers },
        credentials: 'include', // Include credentials for CORS
      }).then(async r => {
        console.log('[TeamManagement] Organization API response:', r.status, r.statusText);
        console.log('[TeamManagement] Response headers:', Object.fromEntries(r.headers.entries()));
        const responseText = await r.text();
        console.log('[TeamManagement] Organization API response body:', responseText.substring(0, 200));
        if (!r.ok) {
          console.error('[TeamManagement] Organization API error:', r.status, responseText);
          throw new Error(`Organization API failed: ${r.status} ${responseText}`);
        }
        return JSON.parse(responseText);
      });
      
      // Extract organization_id from organization response (always use API response as source of truth)
      const org = orgResponse.organization || orgResponse;
      if (org && org.id) {
        organizationId = org.id;
        console.log('[TeamManagement] Using organization_id from API response:', organizationId);
        // Store it in localStorage for future use
        localStorage.setItem('organizationId', String(organizationId));
      } else {
        console.warn('[TeamManagement] No organization ID found in API response');
      }
      
      console.log('[TeamManagement] Final organization_id to use:', organizationId);
      
      // Now fetch members and invitations with organization_id
      const membersUrl = `${API_BASE_URL}/organizations/current/members${organizationId ? `?organization_id=${organizationId}` : ''}`;
      console.log('[TeamManagement] Members API URL:', membersUrl);
      
      const [membersResponse, invitationsResponse, locationsResponse] = await Promise.all([
        fetch(membersUrl, {
          method: 'GET',
          headers: { ...headers },
          credentials: 'include', // Include credentials for CORS
        }).then(async r => {
          console.log('[TeamManagement] Members API response:', r.status, r.statusText);
          console.log('[TeamManagement] Members API - Authorization header present:', !!headers['Authorization']);
          const responseText = await r.text();
          console.log('[TeamManagement] Members API response body:', responseText.substring(0, 200));
          if (!r.ok) {
            console.error('[TeamManagement] Members API error:', r.status, responseText);
            // Return empty array instead of throwing for members
            return [];
          }
          try {
            return JSON.parse(responseText);
          } catch (e) {
            console.error('[TeamManagement] Failed to parse members response:', e);
            return [];
          }
        }),
        fetch(`${API_BASE_URL}/organizations/current/invitations${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''}`, {
          method: 'GET',
          headers: { ...headers },
          credentials: 'include', // Include credentials for CORS
        }).then(async r => {
          console.log('[TeamManagement] Invitations API response:', r.status, r.statusText);
          if (!r.ok) {
            const errorText = await r.text();
            console.error('[TeamManagement] Invitations API error:', r.status, errorText);
            // Return empty array instead of throwing for invitations
            return [];
          }
          return r.json();
        }),
        fetch(`${API_BASE_URL}/organizations/current/locations${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''}`, {
          method: 'GET',
          headers: { ...headers },
          credentials: 'include',
        }).then(async r => {
          if (!r.ok) return [];
          return r.json();
        }).catch(() => [])
      ]);
      
      const locations = Array.isArray(locationsResponse) ? locationsResponse : (locationsResponse?.data ?? []);

      console.log('[TeamManagement] API responses:', {
        orgResponse,
        membersResponse,
        invitationsResponse,
        locationsCount: locations?.length ?? 0
      });
      
      // org already derived from orgResponse above; same object for members/invitations batch
      const members = Array.isArray(membersResponse) ? membersResponse : 
                     (membersResponse?.members || membersResponse?.data || []);
      const invitations = Array.isArray(invitationsResponse) ? invitationsResponse : 
                         (invitationsResponse?.invitations || invitationsResponse?.data || []);
      
      console.log('[TeamManagement] Parsed data:', {
        org: org?.name || 'No org',
        orgId: org?.id,
        membersCount: members.length,
        invitationsCount: invitations.length,
        currentUserRole: orgResponse.current_user_role
      });
      
      // Check if organization is valid (not "No Organization")
      if (org && org.name && org.name !== 'No Organization' && org.id) {
        setOrganization(org);
        setMembers(members);
        setInvitations(invitations);
        setLocations(locations || []);
        setCurrentUserRole(orgResponse.current_user_role || 'member');
        
        setOrgForm({
          name: org.name || '',
          description: org.description || '',
          organization_type: org.organization_type || 'solo'
        });
      } else {
        // No valid organization - user might not be part of an organization
        console.warn('[TeamManagement] No valid organization found. User might need to be assigned to an organization.');
        setOrganization(null);
        setMembers([]);
        setInvitations([]);
        setLocations([]);
        setCurrentUserRole('member');
        setOrgForm({
          name: '',
          description: '',
          organization_type: 'solo'
        });
      }
    } catch (error) {
      console.error('[TeamManagement] Failed to fetch team data:', error);
      showMessage('error', `Failed to load team data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text, extra = {}) => {
    setMessage({ type, text, ...extra });
    setTimeout(() => setMessage({ type: '', text: '' }), 8000);
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          ...inviteForm,
          source_app: inviteForm.role === 'patient_member' ? 'patient_portal' : 'agentic_rcm',
        }),
      });

      if (!response.ok) {
        throw await readApiError(response, 'Failed to send invitation');
      }

      showMessage('success', 'Invitation sent successfully!');
      setInviteForm({ email: '', role: 'member' });
      fetchTeamData();
    } catch (error) {
      showMessage('error', error.message || 'Failed to send invitation', {
        upgradeRequired: error.upgradeRequired,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw await readApiError(response, 'Failed to update member role');
      }

      showMessage('success', 'Member role updated successfully!');
      fetchTeamData();
    } catch (error) {
      showMessage('error', error.message || 'Failed to update member role', {
        upgradeRequired: error.upgradeRequired,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove member');
      }

      showMessage('success', 'Member removed successfully!');
      fetchTeamData();
    } catch (error) {
      showMessage('error', 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invitation');
      }

      showMessage('success', 'Invitation cancelled');
      fetchTeamData();
    } catch (error) {
      showMessage('error', 'Failed to cancel invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to resend invitation');
      }

      showMessage('success', 'Invitation resent successfully!');
    } catch (error) {
      showMessage('error', 'Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(orgForm),
      });

      if (!response.ok) {
        throw new Error('Failed to update organization');
      }

      showMessage('success', 'Organization updated successfully!');
      fetchTeamData();
    } catch (error) {
      showMessage('error', 'Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  const resetLocationForm = () => {
    setLocationForm({
      name: '', address: '', address_line2: '', city: '', state: '', postal_code: '', country: 'US',
      phone: '', timezone: 'America/New_York', is_primary: false
    });
    setEditingLocationId(null);
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(locationForm),
      });
      if (!response.ok) {
        throw await readApiError(response, 'Failed to add location');
      }
      showMessage('success', 'Location added successfully');
      setShowAddLocationModal(false);
      resetLocationForm();
      fetchTeamData();
    } catch (error) {
      showMessage('error', error?.message || 'Failed to add location', {
        upgradeRequired: error.upgradeRequired,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    if (!editingLocationId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/locations/${editingLocationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(locationForm),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update location');
      }
      showMessage('success', 'Location updated successfully');
      setShowEditLocationModal(false);
      resetLocationForm();
      fetchTeamData();
    } catch (error) {
      showMessage('error', error?.message || 'Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!deleteLocationTarget?.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/organizations/current/locations/${deleteLocationTarget.id}`, {
        method: 'DELETE',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error('Failed to remove location');
      showMessage('success', 'Location removed');
      setShowDeleteLocationModal(false);
      setDeleteLocationTarget(null);
      fetchTeamData();
    } catch (error) {
      showMessage('error', error?.message || 'Failed to remove location');
    } finally {
      setLoading(false);
    }
  };

  const openEditLocation = (loc) => {
    setLocationForm({
      name: loc.name || '', address: loc.address || '', address_line2: loc.address_line2 || '',
      city: loc.city || '', state: loc.state || '', postal_code: loc.postal_code || '',
      country: loc.country || 'US', phone: loc.phone || '', timezone: loc.timezone || 'America/New_York',
      is_primary: !!loc.is_primary
    });
    setEditingLocationId(loc.id);
    setShowEditLocationModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (days > 0) return `${days}d ago`;
      if (hours > 0) return `${hours}h ago`;
      if (minutes > 0) return `${minutes}m ago`;
      return 'Just now';
    } catch (e) {
      return dateString;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-yellow-100 text-yellow-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'member': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return '👑';
      case 'admin': return '🛡️';
      case 'member': return '👤';
      default: return '👤';
    }
  };

  const getUserInitials = (member) => {
    if (member.user) {
      const firstName = member.user.first_name || '';
      const lastName = member.user.last_name || '';
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || member.user.email?.charAt(0).toUpperCase() || 'U';
    }
    return (member.first_name?.charAt(0) || '') + (member.last_name?.charAt(0) || '') || member.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserName = (member) => {
    if (member.user) {
      return `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || member.user.email;
    }
    return `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
  };

  const getUserEmail = (member) => {
    return member.user?.email || member.email;
  };

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';
  const isOwner = currentUserRole === 'owner';

  // Detect if running in iframe
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  
  // Show loading only on initial load
  if (loading && !organization && members.length === 0 && invitations.length === 0) {
    return (
      <div className={isInIframe ? "w-full py-2 pr-2 pl-0 bg-gray-50 min-h-screen" : "w-full py-4 pr-4 pl-0 bg-gray-50 min-h-screen"}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading team data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Use real data only - no mock fallback
  const displayOrganization = organization;
  const displayMembers = members;
  const displayInvitations = invitations;

  return (
    <div className={isInIframe ? "w-full py-2 pr-2 pl-0 bg-gray-50 min-h-screen" : "w-full py-4 pr-4 pl-0 bg-gray-50 min-h-screen"}>
      <div className="w-full">
        {/* Header */}
        <div className={isInIframe ? "mb-4" : "mb-8"}>
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
              {displayOrganization ? (
                <p className="text-gray-600 mt-1">
                  {displayOrganization.name} • {displayMembers.length} member{displayMembers.length !== 1 ? 's' : ''}
                </p>
              ) : (
                <p className="text-gray-500 mt-1">
                  No Organization • {displayMembers.length} member{displayMembers.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            {canManageMembers && (
              <button
                onClick={() => setActiveTab('invite')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <UserPlus className="w-5 h-5" />
                <span>Invite Members</span>
              </button>
            )}
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`${isInIframe ? "mb-3" : "mb-6"} p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center space-x-2">
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{message.text}</span>
              </div>
              {message.upgradeRequired && (
                <Link
                  to="/subscription"
                  className="text-sm font-medium underline whitespace-nowrap"
                >
                  View plans & upgrade
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={`border-b border-gray-200 overflow-x-auto ${isInIframe ? "mb-3" : "mb-4 sm:mb-6"}`}>
          <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0">
            <button
              onClick={() => setActiveTab('members')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'members'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Team Members ({displayMembers.length})
            </button>
            {canManageMembers && (
              <>
                <button
                  onClick={() => setActiveTab('invitations')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'invitations'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Invitations ({displayInvitations.length})
                </button>
                <button
                  onClick={() => setActiveTab('invite')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'invite'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Invite New Member
                </button>
                <button
                  onClick={() => setActiveTab('locations')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'locations'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Locations ({locations.length})
                </button>
              </>
            )}
            {isOwner && (
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'settings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Organization Settings
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full">
          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className={isInIframe ? "p-2" : "p-4"}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Members</h3>
              {displayMembers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No team members found</p>
                  {canManageMembers && (
                    <button
                      onClick={() => setActiveTab('invite')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Invite Your First Member
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {displayMembers.map((member) => {
                    const memberRole = member.role || 'member';
                    const memberId = member.id;
                    const isCurrentUser = (member.user?.id || member.id) === user?.id;
                    
                    return (
                      <div
                        key={memberId}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-4 flex-1">
                          {/* Avatar */}
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                            {getUserInitials(member)}
                          </div>
                          
                          {/* Member Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="text-lg font-semibold text-gray-900">
                                {getUserName(member)}
                              </h4>
                              {isCurrentUser && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                  You
                                </span>
                              )}
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-gray-600">{getUserEmail(member)}</p>
                              <div className="flex items-center space-x-3 text-sm text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <span className="text-lg">{getRoleIcon(memberRole)}</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(memberRole)}`}>
                                    {memberRole.toUpperCase()}
                                  </span>
                                </div>
                                {member.joined_at && (
                                  <div className="flex items-center space-x-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>Joined {formatDate(member.joined_at)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        {canManageMembers && !isCurrentUser && memberRole !== 'owner' && (
                          <div className="flex items-center space-x-3 ml-4">
                            {/* Role Select */}
                            <select
                              value={memberRole}
                              onChange={(e) => handleUpdateMemberRole(memberId, e.target.value)}
                              disabled={loading || memberRole === 'owner'}
                              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                            >
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                              {isOwner && <option value="owner">Owner</option>}
                            </select>
                            
                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveMember(memberId)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove member"
                              disabled={loading}
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Invitations Tab */}
          {activeTab === 'invitations' && canManageMembers && (
            <div className={isInIframe ? "p-2" : "p-4"}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Invitations</h3>
              {displayInvitations.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No pending invitations</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {displayInvitations.map((invitation) => {
                    const isExpired = invitation.expires_at && new Date(invitation.expires_at) < new Date();
                    const daysUntilExpiry = invitation.expires_at 
                      ? Math.ceil((new Date(invitation.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
                      : null;
                    
                    return (
                      <div
                        key={invitation.id}
                        className={`p-4 border rounded-lg ${
                          isExpired 
                            ? 'border-red-200 bg-red-50' 
                            : 'border-gray-200 hover:bg-gray-50'
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 font-medium text-sm">
                                {invitation.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {invitation.email}
                                </h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  isExpired 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {isExpired ? 'Expired' : 'Pending'}
                                </span>
                              </div>
                              <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">{getRoleIcon(invitation.role)}</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(invitation.role)}`}>
                                    {invitation.role.toUpperCase()}
                                  </span>
                                </div>
                                {invitation.invited_by && (
                                  <p>
                                    Invited by {invitation.invited_by.first_name} {invitation.invited_by.last_name} • {formatTimeAgo(invitation.created_at)}
                                  </p>
                                )}
                                {invitation.expires_at && (
                                  <p className={isExpired ? 'text-red-600 font-medium' : ''}>
                                    {isExpired 
                                      ? `Expired ${formatTimeAgo(invitation.expires_at)}`
                                      : `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''} (${formatDate(invitation.expires_at)})`
                                    }
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            {!isExpired && (
                              <button
                                onClick={() => handleResendInvitation(invitation.id)}
                                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium transition-colors"
                                disabled={loading}
                              >
                                Resend
                              </button>
                            )}
                            <button
                              onClick={() => handleCancelInvitation(invitation.id)}
                              className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm font-medium transition-colors"
                              disabled={loading}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        
                        {/* Warning for expiring invitations */}
                        {!isExpired && daysUntilExpiry !== null && daysUntilExpiry <= 2 && (
                          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-xs text-yellow-800">
                              ⚠️ This invitation expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}. Consider resending if the user hasn't responded.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Invite Tab */}
          {activeTab === 'invite' && canManageMembers && (
            <div className={isInIframe ? "p-2" : "p-4"}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Invite New Team Member</h3>
                <button
                  onClick={() => setShowRoleHelp(true)}
                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>View Role Guide</span>
                </button>
              </div>
              
              <form onSubmit={handleInviteUser} className="space-y-6 max-w-2xl">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="colleague@example.com"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    id="role"
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    {isOwner && <option value="owner">Owner</option>}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    {inviteForm.role === 'owner' && 'Full access including billing and organization management'}
                    {inviteForm.role === 'admin' && 'Can manage team members and settings'}
                    {inviteForm.role === 'member' && 'Can access and use platform features'}
                  </p>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
              </form>
            </div>
          )}

          {/* Locations Tab */}
          {activeTab === 'locations' && canManageMembers && (
            <div className={isInIframe ? "p-2" : "p-4"}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
                  <p className="text-sm text-gray-500 mt-1">Physical addresses associated with this organization (e.g. offices, clinics).</p>
                </div>
                <button type="button" onClick={() => { resetLocationForm(); setShowAddLocationModal(true); }} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <Building2 className="w-5 h-5" />
                  <span>Add Location</span>
                </button>
              </div>
              {locations.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">No locations yet. Add your first location to get started.</p>
                  <button type="button" onClick={() => { resetLocationForm(); setShowAddLocationModal(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add Location</button>
                </div>
              ) : (
                <div className="space-y-4">
                  {locations.map((loc) => (
                    <div key={loc.id} className="flex justify-between items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{loc.name}</h4>
                          {loc.is_primary && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Primary</span>}
                        </div>
                        {(loc.address || loc.city) && (
                          <p className="text-sm text-gray-600 mt-1">
                            {[loc.address, loc.address_line2, [loc.city, loc.state].filter(Boolean).join(', '), loc.postal_code].filter(Boolean).join(' • ')}
                          </p>
                        )}
                        {loc.phone && <p className="text-sm text-gray-600 mt-0.5">{loc.phone}</p>}
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openEditLocation(loc)} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium">Edit</button>
                        <button type="button" onClick={() => { setDeleteLocationTarget(loc); setShowDeleteLocationModal(true); }} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm font-medium">Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Organization Settings Tab */}
          {activeTab === 'settings' && isOwner && (
            <div className={isInIframe ? "p-2" : "p-4"}>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Organization Settings</h3>
              
              {displayOrganization ? (
                <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Organization ID:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-900 font-mono">
                        {displayOrganization.id || 'N/A'}
                      </span>
                      {displayOrganization.id && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(String(displayOrganization.id));
                            showMessage('success', 'Organization ID copied to clipboard!');
                          }}
                          className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                          title="Copy Organization ID"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Organization Type:</span>
                    <span className="text-sm text-gray-900">
                      {displayOrganization.organization_type === 'solo' && 'Solo (1 provider)'}
                      {displayOrganization.organization_type === 'small_practice' && 'Small Practice (2-10 providers)'}
                      {displayOrganization.organization_type === 'medium_practice' && 'Medium Practice (11-50 providers)'}
                      {displayOrganization.organization_type === 'enterprise' && 'Enterprise (>50 providers)'}
                      {displayOrganization.organization_type === 'practice' && 'Practice'}
                      {!displayOrganization.organization_type && 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Active Members:</span>
                    <span className="text-sm text-gray-900">{displayMembers.length}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-yellow-800">
                    No organization found. Please ensure you are part of an organization.
                  </p>
                </div>
              )}
              
              {displayOrganization && (
                <form onSubmit={handleUpdateOrganization} className="space-y-6 max-w-2xl">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    id="orgName"
                    value={orgForm.name}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="orgDescription" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="orgDescription"
                    value={orgForm.description}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, description: e.target.value }))}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="orgType" className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Type
                  </label>
                  <select
                    id="orgType"
                    value={orgForm.organization_type}
                    onChange={(e) => setOrgForm(prev => ({ ...prev, organization_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="solo">Solo (1 provider)</option>
                    <option value="small_practice">Small Practice (2-10 providers)</option>
                    <option value="medium_practice">Medium Practice (11-50 providers)</option>
                    <option value="enterprise">Enterprise (>50 providers)</option>
                  </select>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Updating...' : 'Update Organization'}
                </button>
              </form>
              )}
            </div>
          )}
        </div>

        {/* Role Help Modal */}
        {showRoleHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className={`flex items-center justify-between border-b border-gray-200 ${isInIframe ? "p-2" : "p-4"}`}>
                <h2 className="text-xl font-semibold text-gray-900">Role Guide</h2>
                <button
                  onClick={() => setShowRoleHelp(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className={`overflow-y-auto max-h-[calc(90vh-120px)] ${isInIframe ? "p-2" : "p-4"}`}>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">👑 Owner</h3>
                    <p className="text-gray-600 mb-2">Full access including billing and organization management</p>
                    <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                      <li>Manage subscription and billing</li>
                      <li>Transfer ownership</li>
                      <li>All admin permissions</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">🛡️ Admin</h3>
                    <p className="text-gray-600 mb-2">Can manage team members and settings</p>
                    <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                      <li>Invite and manage team members</li>
                      <li>Manage organization settings</li>
                      <li>View billing information</li>
                      <li>All member permissions</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">👤 Member</h3>
                    <p className="text-gray-600 mb-2">Can access and use the platform features</p>
                    <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                      <li>Access dashboard and features</li>
                      <li>View organization data</li>
                      <li>Use platform within plan limits</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className={`flex justify-end border-t border-gray-200 bg-gray-50 ${isInIframe ? "p-2" : "p-4"}`}>
                <button
                  onClick={() => setShowRoleHelp(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Location Modal */}
        {showAddLocationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200"><h2 className="text-xl font-semibold text-gray-900">Add Location</h2></div>
              <form onSubmit={handleCreateLocation} className="p-6 space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" required value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Main Office" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input type="text" value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Address line 2</label><input type="text" value={locationForm.address_line2} onChange={(e) => setLocationForm({ ...locationForm, address_line2: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><input type="text" value={locationForm.city} onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">State</label><input type="text" value={locationForm.state} onChange={(e) => setLocationForm({ ...locationForm, state: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Postal code</label><input type="text" value={locationForm.postal_code} onChange={(e) => setLocationForm({ ...locationForm, postal_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Country</label><input type="text" value={locationForm.country} onChange={(e) => setLocationForm({ ...locationForm, country: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" value={locationForm.phone} onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label><input type="text" value={locationForm.timezone} onChange={(e) => setLocationForm({ ...locationForm, timezone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div className="flex items-center"><input type="checkbox" id="loc-primary-add-rcm" checked={locationForm.is_primary} onChange={(e) => setLocationForm({ ...locationForm, is_primary: e.target.checked })} className="rounded border-gray-300" /><label htmlFor="loc-primary-add-rcm" className="ml-2 text-sm text-gray-700">Set as primary location</label></div>
                <div className="flex justify-end space-x-3 pt-4"><button type="button" onClick={() => { setShowAddLocationModal(false); resetLocationForm(); }} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button><button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Adding...' : 'Add Location'}</button></div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Location Modal */}
        {showEditLocationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200"><h2 className="text-xl font-semibold text-gray-900">Edit Location</h2></div>
              <form onSubmit={handleUpdateLocation} className="p-6 space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" required value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input type="text" value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Address line 2</label><input type="text" value={locationForm.address_line2} onChange={(e) => setLocationForm({ ...locationForm, address_line2: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><input type="text" value={locationForm.city} onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">State</label><input type="text" value={locationForm.state} onChange={(e) => setLocationForm({ ...locationForm, state: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div></div>
                <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">Postal code</label><input type="text" value={locationForm.postal_code} onChange={(e) => setLocationForm({ ...locationForm, postal_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Country</label><input type="text" value={locationForm.country} onChange={(e) => setLocationForm({ ...locationForm, country: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" value={locationForm.phone} onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label><input type="text" value={locationForm.timezone} onChange={(e) => setLocationForm({ ...locationForm, timezone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div className="flex items-center"><input type="checkbox" id="loc-primary-edit-rcm" checked={locationForm.is_primary} onChange={(e) => setLocationForm({ ...locationForm, is_primary: e.target.checked })} className="rounded border-gray-300" /><label htmlFor="loc-primary-edit-rcm" className="ml-2 text-sm text-gray-700">Set as primary location</label></div>
                <div className="flex justify-end space-x-3 pt-4"><button type="button" onClick={() => { setShowEditLocationModal(false); resetLocationForm(); }} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button><button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Updating...' : 'Update Location'}</button></div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Location Confirmation */}
        {showDeleteLocationModal && deleteLocationTarget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Remove Location</h2>
              <p className="text-gray-600 mb-6">Are you sure you want to remove <strong>{deleteLocationTarget.name}</strong>? This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => { setShowDeleteLocationModal(false); setDeleteLocationTarget(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleDeleteLocation} disabled={loading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">{loading ? 'Removing...' : 'Remove Location'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
